import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { IPerson } from '../interfaces/models/IPerson';
import { getPersonByUserId } from '../Services/Person';
import { getUserPreferences } from '../Services/UserPreferences';
import { AccessTypeWithStaff } from '../Enums';
import { normalizeRoleToKey } from '../Enums/UserEnums';
import { FirmContactView } from '../interfaces/models/IFirmContact';
import { getFirmContactByFirmId } from '../Services/FirmContact';
import { getFirmById } from '../Services/Firm';
import { EventBusValues } from '../utils/EventBus';
import { PersonEmpty } from '../redux/emptyStates/Person';
import { isJwtExpired } from '../utils/jwtExpiry';
import { logout } from '../utils/logout';

interface DecodedToken {
  exp?: number;
  given_name: string;
  family_name: string;
  name: string;
  oid: string;
  sub: string;
  access: string;
  emails: Array<string>;
  extension_WSG_Permissions?: string;
}

type AuthContextType = {
  isAuthenticated: boolean;
  tokenDecoded: DecodedToken | null;
  loggedPerson: IPerson | undefined;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  _2FAuth: boolean | null;
  set2FAuth: React.Dispatch<React.SetStateAction<boolean | null>>;
  isAuthTransitionActive: boolean;
  setAuthTransitionActive: React.Dispatch<React.SetStateAction<boolean>>;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  tokenDecoded: null,
  loggedPerson: undefined,
  setIsAuthenticated: () => {},
  _2FAuth: null,
  set2FAuth: () => {},
  isAuthTransitionActive: false,
  setAuthTransitionActive: () => {},
});

export const useAuth = (): AuthContextType => useContext(AuthContext);

const getCookie = (name: string) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
};

const readStoredTokenClaims = (): { token: string | null; claims: DecodedToken | null } => {
  const token = sessionStorage.getItem("token") || getCookie("token");
  try {
    const raw = sessionStorage.getItem("token_decoded") || getCookie("token_decoded");
    if (!raw) {
      return { token, claims: null };
    }
    return { token, claims: JSON.parse(raw) as DecodedToken };
  } catch {
    return { token, claims: null };
  }
};

const isStoredSessionValid = (): boolean => {
  const { token, claims } = readStoredTokenClaims();
  return Boolean(token && claims && !isJwtExpired(claims));
};

function resolveFirmAdminAccessSync(
  person: Pick<IPerson, "role">,
  tokenPermission: string | undefined,
): boolean | null {
  if (normalizeRoleToKey(person?.role) === "dataAdmin") {
    return true;
  }
  if (tokenPermission && tokenPermission !== AccessTypeWithStaff.member) {
    return true;
  }
  return null;
}

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [_2FAuth, set2FAuth] = useState<boolean | null>(null);
  const [loggedPerson, setLoggedPerson] = useState<IPerson | undefined>();
  const [isAuthTransitionActive, setAuthTransitionActive] = useState(false);
  /** Bumped after syncing cookie/JWT into sessionStorage so the next render re-reads claims. */
  const [, bumpSessionHydration] = useState(0);

  useLayoutEffect(() => {
    try {
      const token = sessionStorage.getItem("token") || getCookie("token");
      if (!token) {
        return;
      }

      let payload: DecodedToken;
      try {
        payload = jwtDecode(token) as DecodedToken;
      } catch {
        return;
      }
      if (isJwtExpired(payload)) {
        return;
      }

      let needsRerender = false;
      if (!sessionStorage.getItem("token")) {
        sessionStorage.setItem("token", token);
        needsRerender = true;
      }

      const hasDecoded =
        sessionStorage.getItem("token_decoded") || getCookie("token_decoded");
      if (!hasDecoded) {
        sessionStorage.setItem("token_decoded", JSON.stringify(payload));
        needsRerender = true;
      }

      if (needsRerender) {
        bumpSessionHydration((n: number) => n + 1);
      }
    } catch {
      /* ignore invalid session material */
    }
  }, []);

  const tokenDecodedString = sessionStorage.getItem("token_decoded") || getCookie("token_decoded");
  const tokenDecoded: DecodedToken | null = useMemo(() => {
    if (!tokenDecodedString) {
      return null;
    }
    try {
      return JSON.parse(tokenDecodedString) as DecodedToken;
    } catch {
      return null;
    }
  }, [tokenDecodedString]);
  
  const isFetchingUserInfo = useRef(false);
  const lastTokenOid = useRef<string | undefined>();

  const getFirmContactIds = useCallback(async (firmId: string) => {
    try {
      const firmContacts:FirmContactView[] = await getFirmContactByFirmId(firmId)
      const firmContact = firmContacts?.[0]
      if (!firmContact) {
        return []
      }
      return [
        firmContact.primaryContact,
        firmContact.secondaryContact,
        firmContact.accountingContact,
        firmContact.itContact,
        firmContact.socialMediaContact,
        firmContact.hrContact,
        ...(firmContact.marketingContacts ?? []),
        ...(firmContact.liaisonContacts ?? []),
        ...(firmContact.otherContacts ?? []),
      ]
        .filter((contact): contact is NonNullable<typeof contact> => Boolean(contact))
        .map(contact => contact.id)
    } catch (error) {
      console.error(error)
      return []
    }
  }, []);

  const getFirmContactDashboardAccess = useCallback(async (person: IPerson): Promise<boolean> => {
    if (!person?.firm || !person.id) {
      return false;
    }
    const contactIds = await getFirmContactIds(person.firm);
    return contactIds.includes(person.id);
  }, [getFirmContactIds]);

  const getUserInfo = useCallback(async (userId: string) => {
    if (isFetchingUserInfo.current) {
      return;
    }

    isFetchingUserInfo.current = true;
    try {
      const [response, userPrefs] = await Promise.all([
        getPersonByUserId(userId),
        getUserPreferences().catch(() => null),
      ]);

      if (!isStoredSessionValid()) {
        return;
      }
      const { claims } = readStoredTokenClaims();

      if (!response) {
        const oid = claims?.oid ?? userId;
        const displayFromToken =
          claims?.name?.trim() ||
          [claims?.given_name, claims?.family_name].filter(Boolean).join(' ').trim();
        const mergedFromToken: IPerson = {
          ...PersonEmpty,
          id: oid,
          userId: oid,
          name: claims?.given_name ?? PersonEmpty.name,
          lastName: claims?.family_name ?? PersonEmpty.lastName,
          displayName: displayFromToken || PersonEmpty.displayName,
          firmAdminAccess: false,
          ...(userPrefs?.preferences && { preferences: userPrefs.preferences }),
          twoFactorAuthentication: userPrefs?.twoFactorAuthentication,
        };
        setLoggedPerson(mergedFromToken);
        return;
      }

      const tokenPermission = tokenDecoded?.extension_WSG_Permissions;
      const syncFirmAdminAccess = resolveFirmAdminAccessSync(
        response,
        tokenPermission,
      );
      const personWithoutLegacyTwoFactorAuthentication = { ...response } as IPerson & {
        twoFactorAuthentication?: unknown;
      };
      delete personWithoutLegacyTwoFactorAuthentication.twoFactorAuthentication;

      const basePerson = {
        ...personWithoutLegacyTwoFactorAuthentication,
        ...(userPrefs?.preferences && { preferences: userPrefs.preferences }),
        twoFactorAuthentication: userPrefs?.twoFactorAuthentication,
      };

      if (syncFirmAdminAccess !== null) {
        setLoggedPerson({
          ...basePerson,
          firmAdminAccess: syncFirmAdminAccess,
        });
      }

      const [firmAdminAccess, firm] = await Promise.all([
        syncFirmAdminAccess !== null
          ? Promise.resolve(syncFirmAdminAccess)
          : getFirmContactDashboardAccess(response),
        response.firm ? getFirmById(response.firm) : Promise.resolve(undefined),
      ]);

      const mergedPerson: IPerson = {
        ...basePerson,
        firmAdminAccess,
        firmLegacyId: firm?.legacyId,
        firmLogo: firm?.logo,
      };
      if (!isStoredSessionValid()) {
        return;
      }
      setLoggedPerson(mergedPerson);
    } catch (error) {
      console.error("Error fetching user info:", error);
    } finally {
      isFetchingUserInfo.current = false;
    }
  }, [getFirmContactDashboardAccess, tokenDecoded?.extension_WSG_Permissions]);

  useEffect(() => {
    if (!tokenDecoded || !tokenDecoded.oid) {
      return;
    }
    if (isJwtExpired(tokenDecoded)) {
      return;
    }

    const token = sessionStorage.getItem("token") || getCookie("token");
    if (!token) {
      return;
    }

    if (loggedPerson) {
      return;
    }

    if (lastTokenOid.current === tokenDecoded.oid) {
      return;
    }

    lastTokenOid.current = tokenDecoded.oid;
    getUserInfo(tokenDecoded.oid);

    return () => {
      lastTokenOid.current = undefined;
    };
  }, [tokenDecoded, loggedPerson, getUserInfo]);

  useEffect(() => {
    const token = sessionStorage.getItem("token") || getCookie("token");

    if (tokenDecoded && isJwtExpired(tokenDecoded)) {
      lastTokenOid.current = undefined;
      setLoggedPerson(undefined);
      void logout(false);
      setIsAuthenticated(false);
      return;
    }

    if (token && tokenDecoded && !isJwtExpired(tokenDecoded)) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      if (!tokenDecoded) {
        setLoggedPerson(undefined);
      }
    }
  }, [tokenDecoded]);

  useEffect(() => {
    if (!isAuthenticated || !tokenDecoded?.exp) {
      return;
    }

    const msUntilExpiry = tokenDecoded.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      lastTokenOid.current = undefined;
      setLoggedPerson(undefined);
      setIsAuthenticated(false);
      void logout(false);
    }, msUntilExpiry);

    return () => clearTimeout(timer);
  }, [isAuthenticated, tokenDecoded]);


  useEffect(function updateLoggedInPerson() {
    const handleUpdateLoggedPerson = (event: Event) => {
      const customEvent = event as CustomEvent<IPerson>;
      const { detail } = customEvent;
      setLoggedPerson(detail);
    };

    document.addEventListener(EventBusValues.UpdateLoggedPerson, handleUpdateLoggedPerson);

    return () => {
      document.removeEventListener(EventBusValues.UpdateLoggedPerson, handleUpdateLoggedPerson);
    };
  }, []);


  return (
    <AuthContext.Provider value={{ isAuthenticated, tokenDecoded, loggedPerson, setIsAuthenticated, _2FAuth, set2FAuth, isAuthTransitionActive, setAuthTransitionActive }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
